import { useMemo, useState } from 'react';
import { Bell, Receipt, Plus, Minus, ShoppingCart, ArrowLeft, Wheat, Leaf, Sprout, Flame, QrCode, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { money } from '@/components/tables/tableStates';
import { ProductPhoto } from '../staff/OrderTaker';

const FILTERS = [['sin_tacc', 'Sin TACC', Wheat], ['vegetariano', 'Vegetariano', Leaf], ['vegano', 'Vegano', Sprout], ['picante', 'Picante', Flame]];
const hasAttr = (x, f) => x[f] || (f === 'vegetariano' && x.vegano);
const STEPS = [['Recibido', ['Nuevo', 'Confirmado', 'Pendiente']], ['En preparación', ['En preparación']], ['Listo', ['Listo']], ['Entregado', ['Entregado']]];
const stepOf = (s) => STEPS.findIndex(([, list]) => list.includes(s));

const Head = ({ business, unit, onBack, title }) => (
  <header className="bg-[#0b1220] text-white px-4 pt-4 pb-3" data-surface="dark">
    {title ? (
      <div className="flex items-center gap-3"><button onClick={onBack} aria-label="Volver" data-testid="cm-back"><ArrowLeft className="w-5 h-5" /></button><h1 className="text-xl font-bold text-white">{title}</h1></div>
    ) : (
      <div className="flex items-center gap-3">
        {business.logo_url ? <img src={business.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <span className="w-10 h-10 rounded-lg bg-white/10 grid place-items-center font-bold">{business.name?.[0]}</span>}
        <div className="min-w-0"><div className="font-bold uppercase tracking-wide truncate">{business.name}</div><div className="text-xs text-slate-300">{business.business_type === 'beach_club' ? 'Parador' : 'Restaurante'}</div></div>
        <span className="ml-auto rounded-lg bg-white/10 px-3 py-1 text-sm font-semibold" data-testid="cm-unit">{unit.name}</span>
      </div>
    )}
  </header>
);

const Timeline = ({ order }) => {
  const cur = stepOf(order.status);
  return (
    <ol className="space-y-3 mt-3" data-testid={`cm-timeline-${order.order_id}`}>
      {STEPS.map(([label], i) => (
        <li key={label} className="flex items-center gap-3">
          <span className={`w-7 h-7 rounded-full grid place-items-center ${i <= cur ? 'bg-[#15803d] text-white' : 'bg-muted text-muted-foreground'}`}>{i <= cur ? <Check className="w-4 h-4" /> : i + 1}</span>
          <span className={i <= cur ? 'font-semibold' : 'text-muted-foreground'}>{label}</span>
        </li>
      ))}
    </ol>
  );
};

export default function ModernCustomerMenu(p) {
  const { business, unit, categories, products, cart, setCart, myOrders, callWaiter, requestBill, waiterCalled, billRequested, callingWaiter,
    submitOrder, submitting, itemPrice, cartTotal, generalNote, setGeneralNote, needsCheckIn, participantName, setParticipantName, checkIn } = p;
  const [view, setView] = useState('menu');
  const [cat, setCat] = useState(categories[0]?.category_id);
  const [filters, setFilters] = useState([]);
  const list = useMemo(() => products.filter(x => x.is_available !== false && (!cat || x.category_id === cat) && filters.every(f => hasAttr(x, f))), [products, cat, filters]);
  const add = (product) => {
    const i = cart.findIndex(it => it.product_id === product.product_id && !it.notes && !it.extras?.length);
    if (i >= 0) return setCart(cart.map((it, j) => j === i ? { ...it, quantity: it.quantity + 1 } : it));
    setCart([...cart, { product_id: product.product_id, product, quantity: 1, variant: product.variants?.[0]?.name || null, extras: [], notes: '' }]);
  };
  const setQty = (i, d) => setCart(cart.map((it, j) => j === i ? { ...it, quantity: it.quantity + d } : it).filter(it => it.quantity > 0));
  const count = cart.reduce((a, i) => a + i.quantity, 0);
  if (view === 'cart') return (
    <div className="min-h-screen bg-background pb-28" data-testid="cm-cart">
      <Head business={business} unit={unit} title="Tu pedido" onBack={() => setView('menu')} />
      <ul className="p-4 space-y-3">
        {cart.map((it, i) => (
          <li key={i} className="m-card p-3 flex gap-3">
            <ProductPhoto url={it.product.image_url} alt={it.product.name} className="w-20 h-20 rounded-lg shrink-0" />
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex justify-between gap-2"><span className="font-semibold">{it.product.name}</span><span className="font-bold">{money(itemPrice(it))}</span></div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(i, -1)} aria-label="Menos" data-testid={`cm-minus-${i}`}><Minus className="w-3 h-3" /></Button>
                <span className="w-6 text-center font-bold">{it.quantity}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setQty(i, 1)} aria-label="Más" data-testid={`cm-plus-${i}`}><Plus className="w-3 h-3" /></Button>
              </div>
              <Input className="h-9 text-sm" placeholder="Nota (ej. sin cebolla)" value={it.notes} onChange={e => setCart(cart.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))} data-testid={`cm-item-note-${i}`} />
            </div>
          </li>
        ))}
        {!cart.length && <li className="text-center text-muted-foreground py-10">Todavía no agregaste nada</li>}
      </ul>
      <div className="px-4 space-y-2">
        <label className="text-sm font-semibold" htmlFor="cm-note">Notas generales</label>
        <Input id="cm-note" placeholder="Ej. traigan los platos de a poco" value={generalNote} onChange={e => setGeneralNote(e.target.value)} data-testid="cm-general-note" />
        {needsCheckIn && <Input placeholder="Tu nombre" value={participantName} onChange={e => setParticipantName(e.target.value)} data-testid="cm-name" />}
        <div className="flex justify-between text-xl font-bold pt-2"><span>Total</span><span data-testid="cm-total">{money(cartTotal)}</span></div>
      </div>
      <div className="fixed bottom-0 inset-x-0 p-4 bg-background border-t border-border">
        <Button className="w-full h-12 text-base bg-[#2563EB] hover:bg-[#1d4ed8] text-white" disabled={!cart.length || submitting || (needsCheckIn && !participantName)}
          onClick={async () => { const who = needsCheckIn ? await checkIn() : null; if (needsCheckIn && !who) return; await submitOrder(who); setView('account'); }} data-testid="cm-send-btn">Enviar a cocina</Button>
      </div>
    </div>
  );
  if (view === 'account') return (
    <div className="min-h-screen bg-background pb-10" data-testid="cm-account">
      <Head business={business} unit={unit} title="Mi cuenta" onBack={() => setView('menu')} />
      <div className="p-4 space-y-4">
        <div><h2 className="text-xl font-bold">{unit.name}</h2></div>
        {myOrders.map(o => (
          <section key={o.order_id} className="m-card p-4">
            <div className="flex justify-between"><h3 className="font-bold">Pedido #{o.order_id.slice(-4)}</h3><span className="text-sm">{new Date(o.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span></div>
            <ul className="text-sm mt-2">{(o.items || []).map((it, i) => <li key={i}>{it.quantity} · {it.product_name}{it.notes ? ` — ${it.notes}` : ''}</li>)}</ul>
            <Timeline order={o} />
          </section>
        ))}
        {!myOrders.length && <p className="text-muted-foreground text-center py-6">Todavía no hiciste pedidos</p>}
        <Button className="w-full h-12 bg-[#b91c1c] hover:bg-[#991b1b] text-white" onClick={requestBill} disabled={billRequested} data-testid="cm-account-bill-btn"><Receipt className="w-4 h-4 mr-2" />{billRequested ? 'Cuenta pedida' : 'Pedir la cuenta'}</Button>
        <Button className="w-full h-12" variant="outline" disabled data-testid="cm-pay-share-btn"><QrCode className="w-4 h-4 mr-2" />Pagar mi parte · próximamente</Button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-background pb-28" data-testid="modern-customer-menu">
      <Head business={business} unit={unit} />
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="h-11 border-[#fecaca] text-[#991b1b] bg-[#fef2f2]" onClick={callWaiter} disabled={callingWaiter || waiterCalled} data-testid="cm-call-waiter"><Bell className="w-4 h-4 mr-2" />{waiterCalled ? 'Mozo avisado' : 'Llamar al mozo'}</Button>
          <Button variant="outline" className="h-11 border-[#ddd6fe] text-[#5b21b6] bg-[#f5f3ff]" onClick={requestBill} disabled={billRequested} data-testid="cm-request-bill"><Receipt className="w-4 h-4 mr-2" />{billRequested ? 'Cuenta pedida' : 'Pedir la cuenta'}</Button>
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {FILTERS.map(([k, l, I]) => {
            const on = filters.includes(k);
            return <button key={k} aria-pressed={on} onClick={() => setFilters(on ? filters.filter(f => f !== k) : [...filters, k])} data-testid={`cm-filter-${k}`}
              className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-sm ${on ? 'bg-[#2563EB] text-white border-[#2563EB]' : 'border-border'}`}><I className="w-4 h-4" />{l}</button>;
          })}
          <button onClick={() => setView('account')} className="shrink-0 h-9 px-3 rounded-full border border-border text-sm" data-testid="cm-my-account">Mi cuenta</button>
        </div>
        <div className="flex gap-4 overflow-x-auto border-b border-border" role="tablist">
          {categories.map(c => <button key={c.category_id} role="tab" aria-selected={cat === c.category_id} onClick={() => setCat(c.category_id)} className="m-tab shrink-0 pb-2 text-sm font-medium" data-testid={`cm-cat-${c.category_id}`}>{c.name}</button>)}
        </div>
        <ul className="space-y-3">
          {list.map(x => (
            <li key={x.product_id} className="m-card p-3 flex gap-3" data-testid={`cm-product-${x.product_id}`}>
              <div className="relative shrink-0">
                <ProductPhoto url={x.image_url} alt={x.name} className="w-24 h-24 rounded-lg" />
                {x.destacado && <span className="absolute top-1 left-1 rounded bg-[#b91c1c] text-white text-[10px] font-bold px-1.5 py-0.5">La más pedida</span>}
                {!x.destacado && x.sin_tacc && <span className="absolute top-1 left-1 rounded bg-[#15803d] text-white text-[10px] font-bold px-1.5 py-0.5">Sin TACC</span>}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold">{x.name}</h3>
                {x.description && <p className="text-xs text-muted-foreground line-clamp-2">{x.description}</p>}
                {FILTERS.some(([k]) => x[k]) && <div className="flex flex-wrap gap-1 mt-1" data-testid={`cm-tags-${x.product_id}`}>
                  {FILTERS.filter(([k]) => x[k]).map(([k, l, I]) => <span key={k} className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[11px] font-medium"><I className="w-3 h-3" aria-hidden="true" />{l}</span>)}
                </div>}
                <div className="flex items-end justify-between mt-2"><span className="font-bold">{money(x.price)}</span>
                  <Button size="icon" className="h-9 w-9 bg-[#2563EB] hover:bg-[#1d4ed8] text-white" onClick={() => add(x)} aria-label={`Agregar ${x.name}`} data-testid={`cm-add-${x.product_id}`}><Plus className="w-4 h-4" /></Button></div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {count > 0 && (
        <button onClick={() => setView('cart')} className="fixed bottom-4 inset-x-4 h-14 rounded-full bg-[#2563EB] text-white font-semibold flex items-center justify-center gap-3 shadow-lg" data-testid="cm-view-cart">
          Ver pedido · {count} ítems · {money(cartTotal)}<ShoppingCart className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
